/*!
 * Copyright (c) Microsoft Corporation.
 * All rights reserved. See LICENSE in the project root for license information.
 */

import { ICommerceApiSettings } from '@msdyn365-commerce/core-internal';
import { generateSwatchImageUrl, StringExtensions } from '@msdyn365-commerce-modules/retail-actions';
import classnames from 'classnames';
import * as React from 'react';

/**
 * Dimension item data.
 */
export interface IProductDimensionsMatrixDimensionItemData {
    value: string;
    itemId: string;
    colorHexCode?: string;
    imageUrl?: string;
}

/**
 * Swatch item configuration.
 */
export interface IProductDimensionsMatrixDimensionItemProps {
    className: string;
    apiSettings: ICommerceApiSettings;

    item: IProductDimensionsMatrixDimensionItemData;
}

/**
 * Represents a single item for the dimension.
 * @param props - Item configuration.
 * @returns React node.
 */
export const ProductDimensionsMatrixDimensionItemComponent: React.FC<IProductDimensionsMatrixDimensionItemProps> = (
    props: IProductDimensionsMatrixDimensionItemProps
) => {
    const { className, item } = props;
    const { itemId, value, colorHexCode, imageUrl } = item;

    const style: React.CSSProperties = {};

    const hasColor = !StringExtensions.isNullOrWhitespace(colorHexCode);
    const hasImage = !StringExtensions.isNullOrWhitespace(imageUrl);

    let swatchContent: React.ReactElement | undefined;
    const swatchContainerClassName = 'msc-swatch-container';
    const swatchContentClassName = `${swatchContainerClassName}__item`;
    if (hasColor) {
        style.backgroundColor = colorHexCode;
        swatchContent = (
            <span
                key={itemId}
                className={classnames(swatchContentClassName, `${swatchContentClassName}__colored`)}
                style={style}
                title={value}
                aria-label={value}
            />
        );
    } else if (hasImage) {
        const fullImageUrl = generateSwatchImageUrl(imageUrl!, props.apiSettings);
        swatchContent = (
            <img
                key={itemId}
                src={fullImageUrl}
                className={classnames(swatchContentClassName, `${swatchContentClassName}__image`)}
                style={style}
                title={value}
                aria-label={value}
                alt={value}
            />
        );
    }

    return (
        <span className={classnames(className, `${className}-${itemId}`)}>
            {swatchContent && <span className={swatchContainerClassName}>{swatchContent}</span>}
            <span className={`${className}__value`}>{value}</span>
        </span>
    );
};
